// A draft context as plain data (BACKLOG D10, MOCK_DRAFT.md §4).
//
// THE ENGINE'S CONTEXT HAS FUNCTIONS IN IT — a position lookup and a keyed
// random — and a host that keeps one between requests, or sends one over the
// wire, cannot keep a function. These are the context's data and the rebuild
// from it: the live room stores the parts and wakes from them, and a mock's
// kit carries them to the browser, so both draft exactly as the server would.

import type { DraftContext } from './engine';
import type { ResolvePlan } from './resolve';
import type { DraftClub, DraftFacts } from './style';

/** A stable number in [0, 1) from a string. FNV-1a, so no dependency and no clock. */
export function hashUnit(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return ((h >>> 0) % 100000) / 100000;
}

/**
 * A draft context as plain data, for a host that must keep one between
 * requests: the live room stores this and rebuilds the functions from it, so
 * a room woken from hibernation drafts exactly as the one that went to sleep.
 */
export interface DraftContextParts {
  rankings: string[];
  positions: Record<string, string>;
  starterNeeds: Record<string, number>;
  maxPerPosition: Record<string, number>;
  leagueCaps: Record<string, number>;
  latePositions: string[];
  plans: Record<string, ResolvePlan>;
  /** What `random` is keyed on: the draft's own id, so a replay matches. */
  seed: string;
  /**
   * What the bots' draft styles read (AI_MANAGERS.md §4.7). Absent from a
   * room prepared before 2026-10-15, whose bots draft by the list as they did.
   * Each player's own facts travel with the player, where a host keeps the
   * pool in pieces (`LivePlayer.facts`), and are handed in beside these.
   */
  sport?: string;
  styleSeed?: string;
  clubs?: Record<string, DraftClub>;
}

export function contextParts(
  context: DraftContext,
  seed: string,
  /**
   * Players on no list whose positions the engine still reads: a dynasty's
   * carried rosters, which its teams' needs count (`DraftState.held`).
   */
  also: readonly string[] = [],
): DraftContextParts {
  return {
    rankings: [...context.rankings],
    positions: Object.fromEntries(
      [...context.rankings, ...also].map((id) => [id, context.positionOf(id)]),
    ),
    starterNeeds: { ...context.starterNeeds },
    maxPerPosition: { ...context.maxPerPosition },
    leagueCaps: { ...(context.leagueCaps ?? {}) },
    latePositions: [...context.latePositions],
    plans: { ...(context.plans ?? {}) },
    seed,
    ...(context.sport === undefined ? {} : { sport: context.sport }),
    ...(context.styleSeed === undefined ? {} : { styleSeed: context.styleSeed }),
    ...(context.clubs === undefined ? {} : { clubs: { ...context.clubs } }),
  };
}

/** The engine's context from its parts, and each player's facts where the host has them. */
export function contextFromParts(
  parts: DraftContextParts,
  facts?: Readonly<Record<string, DraftFacts>>,
): DraftContext {
  return {
    rankings: parts.rankings,
    positionOf: (id) => parts.positions[id] ?? '',
    starterNeeds: parts.starterNeeds,
    maxPerPosition: parts.maxPerPosition,
    leagueCaps: parts.leagueCaps,
    latePositions: parts.latePositions,
    // The same keyed random `draftContextFor` gives, from the same seed.
    random: (key) => hashUnit(`${parts.seed}:${key}`),
    plans: parts.plans,
    ...(parts.sport === undefined ? {} : { sport: parts.sport }),
    ...(parts.styleSeed === undefined ? {} : { styleSeed: parts.styleSeed }),
    ...(parts.clubs === undefined ? {} : { clubs: parts.clubs }),
    ...(facts === undefined ? {} : { facts }),
  };
}
